import React, { useState, useEffect } from 'react';
import { Download, LogOut, RefreshCw, Users, FileText, UserPlus, List, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

// Use empty string for same-origin requests (Emergent deployment pattern)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Admin = () => {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Navigation state
  const [activeSection, setActiveSection] = useState('formulaires'); // 'formulaires' or 'admins'
  const [activeTab, setActiveTab] = useState('candidatures');
  
  // Data states
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [adminsList, setAdminsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Selection states for bulk actions
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Admin creation form
  const [newAdminData, setNewAdminData] = useState({
    email: '',
    password: '',
    full_name: ''
  });

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchData();
    }
  }, [token, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      if (!response.ok) {
        throw new Error('Identifiants invalides');
      }

      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem('adminToken', data.access_token);
      setIsAuthenticated(true);
      
      toast({
        title: "Connexion réussie",
        description: "Bienvenue dans l'espace d'administration",
      });
    } catch (error) {
      toast({
        title: "Erreur de connexion",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setApplications([]);
    setContacts([]);
  };

  const fetchData = async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      if (activeTab === 'candidatures') {
        const response = await fetch(`${BACKEND_URL}/api/careers/applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setApplications(data);
        }
      } else {
        const response = await fetch(`${BACKEND_URL}/api/contacts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setContacts(data);
        }
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les données",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  const fetchAdmins = async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminsList(data);
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de charger la liste des admins",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur de connexion",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    if (!newAdminData.email || !newAdminData.password) {
      toast({
        title: "Erreur",
        description: "Email et mot de passe sont requis",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newAdminData)
      });

      if (response.ok) {
        toast({
          title: "Succès",
          description: "Administrateur créé avec succès",
        });
        setNewAdminData({ email: '', password: '', full_name: '' });
        fetchAdmins(); // Refresh the list
      } else {
        const error = await response.json();
        toast({
          title: "Erreur",
          description: error.detail || "Impossible de créer l'administrateur",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur de connexion",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleDeleteAdmin = async (adminId, adminEmail) => {
    // Confirmation dialog
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'administrateur "${adminEmail}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/delete/${adminId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Succès",
          description: `Administrateur "${result.deleted_email}" supprimé avec succès`,
        });
        fetchAdmins(); // Refresh the list
      } else {
        const error = await response.json();
        toast({
          title: "Erreur",
          description: error.detail || "Impossible de supprimer l'administrateur",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur de connexion",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedApplications(applications.map(app => app.id));
      setSelectAll(true);
    } else {
      setSelectedApplications([]);
      setSelectAll(false);
    }
  };

  const handleSelectApplication = (appId) => {
    setSelectedApplications(prev => {
      if (prev.includes(appId)) {
        return prev.filter(id => id !== appId);
      } else {
        return [...prev, appId];
      }
    });
  };

  // Update status
  const handleStatusChange = async (appId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/careers/applications/${appId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Update local state
        setApplications(prev => prev.map(app => 
          app.id === appId ? { ...app, status: newStatus } : app
        ));
        toast({
          title: "Statut mis à jour",
          description: "Le statut de la candidature a été modifié avec succès",
        });
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive"
      });
    }
  };

  // Delete selected applications
  const handleDeleteSelected = async () => {
    if (selectedApplications.length === 0) {
      toast({
        title: "Aucune sélection",
        description: "Veuillez sélectionner au moins une candidature",
        variant: "destructive"
      });
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedApplications.length} candidature(s) ?\n\nCette action est irréversible.`)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/careers/applications/delete-multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(selectedApplications)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Suppression réussie",
          description: `${result.deleted_count} candidature(s) supprimée(s) avec succès`,
        });
        setSelectedApplications([]);
        setSelectAll(false);
        fetchData(); // Refresh the list
      } else {
        throw new Error('Failed to delete applications');
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer les candidatures",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Export selected to CSV
  const exportSelectedToCSV = () => {
    if (selectedApplications.length === 0) {
      toast({
        title: "Aucune sélection",
        description: "Veuillez sélectionner au moins une candidature",
        variant: "destructive"
      });
      return;
    }

    const selectedApps = applications.filter(app => selectedApplications.includes(app.id));
    let csv = 'Date,Prénom,Nom,Email,Téléphone,CV,Statut,Message\n';
    
    selectedApps.forEach(app => {
      const date = new Date(app.submittedAt).toLocaleString('fr-FR');
      const statusLabels = {
        'nouveau': 'Nouveau',
        'valide': 'Validé',
        'rejete': 'Rejeté',
        'en_attente': 'En attente',
        'contacte_tel': 'Contacté (tél)',
        'contacte_email': 'Contacté (email)'
      };
      csv += `"${date}","${app.firstName}","${app.lastName}","${app.email}","${app.phone}","${app.cvOriginalName || app.cvFileName || 'Non fourni'}","${statusLabels[app.status] || app.status}","${app.message.replace(/"/g, '""')}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `candidatures_selection_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast({
      title: "Export réussi",
      description: `${selectedApplications.length} candidature(s) exportée(s)`,
    });
  };



  // Load admins when switching to admin section
  useEffect(() => {
    if (activeSection === 'admins' && token) {
      fetchAdmins();
    }
  }, [activeSection]);


  const exportToCSV = () => {
    const data = activeTab === 'candidatures' ? applications : contacts;
    if (data.length === 0) return;

    let csv = '';
    
    if (activeTab === 'candidatures') {
      csv = 'Date,Prénom,Nom,Email,Téléphone,CV,Message\n';
      data.forEach(app => {
        const date = new Date(app.submittedAt).toLocaleString('fr-FR');
        csv += `"${date}","${app.firstName}","${app.lastName}","${app.email}","${app.phone}","${app.cvFileName || 'Non fourni'}","${app.message.replace(/"/g, '""')}"\n`;
      });
    } else {
      csv = 'Date,Nom,Email,Téléphone,Message\n';
      data.forEach(contact => {
        const date = new Date(contact.submittedAt).toLocaleString('fr-FR');
        csv += `"${date}","${contact.name}","${contact.email}","${contact.phone || 'Non fourni'}","${contact.message.replace(/"/g, '""')}"\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast({
      title: "Export réussi",
      description: `Fichier CSV téléchargé avec succès`,
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Administration</h1>
              <p className="text-gray-600">RT Distribution</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  placeholder="votre@email.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  placeholder="••••••••"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                disabled={isLoggingIn}
              >
                {isLoggingIn ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Administration</h1>
              <p className="text-gray-600 mt-1">Gestion de RT Distribution</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <LogOut size={18} />
              <span>Déconnexion</span>
            </Button>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="bg-white rounded-xl shadow-md mb-8">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveSection('formulaires')}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                activeSection === 'formulaires'
                  ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FileText size={20} />
              <span>Formulaires</span>
            </button>
            <button
              onClick={() => setActiveSection('admins')}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors ${
                activeSection === 'admins'
                  ? 'text-green-600 border-b-2 border-green-600 bg-green-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users size={20} />
              <span>Gestion des Admins</span>
            </button>
          </div>
        </div>

        {/* Content based on active section */}
        {activeSection === 'formulaires' && (
          <>
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-md mb-8">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('candidatures')}
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'candidatures'
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Candidatures ({applications.length})
              </button>
              <button
                onClick={() => setActiveTab('contacts')}
                className={`px-6 py-4 font-medium transition-colors ${
                  activeTab === 'contacts'
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Messages de contact ({contacts.length})
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <Button
                onClick={fetchData}
                variant="outline"
                disabled={isLoading}
                className="flex items-center space-x-2"
              >
                <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
                <span>{isLoading ? 'Chargement...' : 'Actualiser'}</span>
              </Button>
              
              <Button
                onClick={exportToCSV}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 flex items-center space-x-2"
              >
                <Download size={18} />
                <span>Exporter tout</span>
              </Button>
            </div>
            
            {/* Bulk Actions Bar (shown when items are selected) */}
            {selectedApplications.length > 0 && activeTab === 'candidatures' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
                <span className="text-blue-800 font-medium">
                  {selectedApplications.length} candidature(s) sélectionnée(s)
                </span>
                <div className="flex space-x-2">
                  <Button
                    onClick={exportSelectedToCSV}
                    variant="outline"
                    className="flex items-center space-x-2"
                  >
                    <Download size={16} />
                    <span>Exporter la sélection</span>
                  </Button>
                  <Button
                    onClick={handleDeleteSelected}
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 flex items-center space-x-2"
                  >
                    <Trash2 size={16} />
                    <span>Supprimer la sélection</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Data Tables */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {activeTab === 'candidatures' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">CV</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {applications.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                        Aucune candidature pour le moment
                      </td>
                    </tr>
                  ) : (
                    applications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedApplications.includes(app.id)}
                            onChange={() => handleSelectApplication(app.id)}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {new Date(app.submittedAt).toLocaleString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {app.firstName} {app.lastName}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{app.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{app.phone}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {app.cvFileName ? (
                            <a
                              href={`${BACKEND_URL}/api/download/cv/${app.cvFileName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center space-x-1 text-green-600 hover:text-green-700 hover:underline"
                              onClick={(e) => {
                                e.preventDefault();
                                // Use token for authenticated download
                                fetch(`${BACKEND_URL}/api/download/cv/${app.cvFileName}`, {
                                  headers: {
                                    'Authorization': `Bearer ${token}`
                                  }
                                })
                                .then(response => response.blob())
                                .then(blob => {
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = app.cvOriginalName || app.cvFileName;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                })
                                .catch(error => {
                                  console.error('Download error:', error);
                                  toast({
                                    title: "Erreur",
                                    description: "Impossible de télécharger le fichier",
                                    variant: "destructive"
                                  });
                                });
                              }}
                            >
                              <Download size={16} />
                              <span>{app.cvOriginalName || 'Télécharger CV'}</span>
                            </a>
                          ) : (
                            'Non fourni'
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <select
                            value={app.status || 'nouveau'}
                            onChange={(e) => handleStatusChange(app.id, e.target.value)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                              app.status === 'valide' ? 'bg-green-100 text-green-800 border-green-300' :
                              app.status === 'rejete' ? 'bg-red-100 text-red-800 border-red-300' :
                              app.status === 'en_attente' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                              app.status === 'contacte_tel' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                              app.status === 'contacte_email' ? 'bg-purple-100 text-purple-800 border-purple-300' :
                              'bg-gray-100 text-gray-800 border-gray-300'
                            }`}
                          >
                            <option value="nouveau">🆕 Nouveau</option>
                            <option value="valide">✅ Validé</option>
                            <option value="rejete">❌ Rejeté</option>
                            <option value="en_attente">⏸️ En attente</option>
                            <option value="contacte_tel">📞 Contacté (tél)</option>
                            <option value="contacte_email">📧 Contacté (email)</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                          {app.message}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contacts.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        Aucun message de contact pour le moment
                      </td>
                    </tr>
                  ) : (
                    contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {new Date(contact.submittedAt).toLocaleString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{contact.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{contact.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {contact.phone || 'Non fourni'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                          {contact.message}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {/* Admin Management Section */}
        {activeSection === 'admins' && (
          <div className="space-y-8">
            {/* Create New Admin */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center space-x-2 mb-6">
                <UserPlus size={24} className="text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900">Créer un Administrateur</h2>
              </div>
              
              <form onSubmit={handleCreateAdmin} className="space-y-4 max-w-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email *</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      value={newAdminData.email}
                      onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                      required
                      placeholder="admin@rt-distribution.fr"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="admin-fullname">Nom complet</Label>
                    <Input
                      id="admin-fullname"
                      type="text"
                      value={newAdminData.full_name}
                      onChange={(e) => setNewAdminData({ ...newAdminData, full_name: e.target.value })}
                      placeholder="Jean Dupont"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="admin-password">Mot de passe *</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    value={newAdminData.password}
                    onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                    required
                    placeholder="••••••••"
                  />
                  <p className="text-sm text-gray-500">
                    Le mot de passe doit être sécurisé (8+ caractères, majuscules, chiffres, symboles recommandés)
                  </p>
                </div>
                
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  disabled={isLoading}
                >
                  {isLoading ? 'Création...' : 'Créer l\'administrateur'}
                </Button>
              </form>
            </div>

            {/* List of Admins */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <List size={24} className="text-green-600" />
                    <h2 className="text-2xl font-bold text-gray-900">Liste des Administrateurs</h2>
                  </div>
                  <Button
                    onClick={fetchAdmins}
                    variant="outline"
                    disabled={isLoading}
                    className="flex items-center space-x-2"
                  >
                    <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
                    <span>Actualiser</span>
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Créé le</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                          Chargement...
                        </td>
                      </tr>
                    ) : adminsList.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                          Aucun administrateur trouvé
                        </td>
                      </tr>
                    ) : (
                      adminsList.map((admin) => (
                        <tr key={admin.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {admin.email}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {admin.full_name || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {admin.is_active ? (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Actif
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                Inactif
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(admin.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <Button
                              onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                              disabled={isLoading}
                            >
                              <Trash2 size={16} className="mr-1" />
                              Supprimer
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
